import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { QueryKey } from "@tanstack/react-query";

interface ProjectManagersAddUsersContextType {
  projectId: string;
  isAddUsersDialogOpened: boolean;
  setIsAddUsersDialogOpened: (open: boolean) => void;
  managersDirectoryQueryKey: QueryKey;
}

const ProjectManagersAddUsersContext = createContext<
  ProjectManagersAddUsersContextType | undefined
>(undefined);

export const ProjectManagersAddUsersContextProvider = ({
  children,
  isAddUsersDialogOpened,
  setIsAddUsersDialogOpened,
  managersDirectoryQueryKey,
}: PropsWithChildren<
  Pick<
    ProjectManagersAddUsersContextType,
    | "isAddUsersDialogOpened"
    | "setIsAddUsersDialogOpened"
    | "managersDirectoryQueryKey"
  >
>) => {
  const projectId = useProjectId();

  const value = useMemo<ProjectManagersAddUsersContextType>(
    () => ({
      projectId,
      isAddUsersDialogOpened,
      setIsAddUsersDialogOpened,
      managersDirectoryQueryKey,
    }),
    [
      projectId,
      isAddUsersDialogOpened,
      setIsAddUsersDialogOpened,
      managersDirectoryQueryKey,
    ],
  );

  return (
    <ProjectManagersAddUsersContext.Provider value={value}>
      {children}
    </ProjectManagersAddUsersContext.Provider>
  );
};

export const useProjectManagersAddUsersContext = () => {
  const context = useContext(ProjectManagersAddUsersContext);
  if (!context) {
    throw new Error(
      "useProjectManagersAddUsersContext must be used within a ProjectManagersAddUsersContext",
    );
  }
  return context;
};
